// Timing editor state — manages notes, selection, drag, undo for the admin lesson timing editor.

export interface NotePayload {
  beat: number
  note: string
  duration: number
  velocity?: number
}

export interface TimingState {
  notes: NotePayload[]
  lanes: string[]
  mapping: Record<string, number>
  bpm: number
  timeSignatureTop: number
  timeSignatureBottom: number
  durationBeats: number
  selectedIndices: Set<number>
  drag: DragState | null
  snap: SnapSetting
  undoStack: NotePayload[][]
  dirty: boolean
}

export interface DragState {
  noteIdx: number
  startMouseX: number
  startBeat: number
  startLane: string
  mode: "move" | "resize"
}

export type SnapSetting = 0 | 0.25 | 0.125

export function snapBeat(beat: number, snap: SnapSetting): number {
  if (snap === 0) return Math.max(0.5, beat)
  const snapped = Math.round(beat / snap) * snap
  return Math.max(0.5, parseFloat(snapped.toFixed(4)))
}

export function laneY(lane: number, laneCount: number, timelineH: number): number {
  const rowH = timelineH / Math.max(laneCount, 1)
  return timelineH - (lane + 1) * rowH
}

export function laneIndexOf(noteName: string, lanes: string[]): number {
  const idx = lanes.indexOf(noteName)
  return idx >= 0 ? idx : 0
}

export function createInitialState(
  notes: NotePayload[],
  mapping: Record<string, number>,
  bpm: number,
  timeSigTop: number,
  timeSigBottom: number,
  durationBeats: number
): TimingState {
  const sorted = Object.entries(mapping).sort((a, b) => a[1] - b[1])
  const lanes = sorted.map(([name]) => name)

  return {
    notes: notes.map((n) => ({ ...n })),
    lanes,
    mapping,
    bpm,
    timeSignatureTop: timeSigTop,
    timeSignatureBottom: timeSigBottom,
    durationBeats,
    selectedIndices: new Set<number>(),
    drag: null,
    snap: 0.25,
    undoStack: [],
    dirty: false,
  }
}

export function pushUndo(state: TimingState): void {
  state.undoStack.push(state.notes.map((n) => ({ ...n })))
  if (state.undoStack.length > 100) state.undoStack.shift()
}

export function undo(state: TimingState): void {
  const prev = state.undoStack.pop()
  if (prev) {
    state.notes = prev
    state.selectedIndices.clear()
    state.dirty = true
  }
}

export function addNote(state: TimingState, note: NotePayload): void {
  pushUndo(state)
  state.notes.push(note)
  state.dirty = true
}

export function deleteSelected(state: TimingState): void {
  if (state.selectedIndices.size === 0) return
  pushUndo(state)
  // Remove from highest index first so indices stay valid
  const sorted = [...state.selectedIndices].sort((a, b) => b - a)
  for (const idx of sorted) {
    state.notes.splice(idx, 1)
  }
  state.selectedIndices.clear()
  state.dirty = true
}

export function moveNote(state: TimingState, idx: number, newBeat: number, newNote: string): void {
  state.notes[idx].beat = snapBeat(newBeat, state.snap)
  state.notes[idx].note = newNote
  state.dirty = true
}

export function resizeNote(state: TimingState, idx: number, newDuration: number): void {
  state.notes[idx].duration = Math.max(0.125, parseFloat(newDuration.toFixed(4)))
  state.dirty = true
}

/** Select notes inside a rectangle defined by (x1,y1)-(x2,y2) in canvas coords. */
export function selectInRect(
  state: TimingState,
  x1: number, y1: number,
  x2: number, y2: number,
  canvasW: number,
  canvasH: number,
  beatToX: (beat: number) => number,
  lookAhead: number
): void {
  const timelineH = canvasH
  const laneCount = state.lanes.length
  const rowH = timelineH / Math.max(laneCount, 1)
  const playheadX = 64 + (canvasW - 64) * 0.24
  const pxPerBeat = (canvasW - playheadX) / lookAhead
  const radius = Math.min(rowH * 0.32, 16)
  const minX = Math.min(x1, x2)
  const maxX = Math.max(x1, x2)
  const minY = Math.min(y1, y2)
  const maxY = Math.max(y1, y2)

  state.selectedIndices.clear()
  for (let i = 0; i < state.notes.length; i++) {
    const n = state.notes[i]
    const laneIdx = laneIndexOf(n.note, state.lanes)
    const nx = beatToX(n.beat)
    const cy = laneY(laneIdx, laneCount, timelineH) + rowH / 2
    const tailW = Math.max(0, n.duration * pxPerBeat)

    // Note center or any part of the tail within the rect
    const noteMinX = nx - radius
    const noteMaxX = nx + radius + tailW
    const noteMinY = cy - radius
    const noteMaxY = cy + radius

    if (noteMaxX >= minX && noteMinX <= maxX && noteMaxY >= minY && noteMinY <= maxY) {
      state.selectedIndices.add(i)
    }
  }
}
